import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import Typo from "@/src/components/atom/Typo";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import colorSet from "@/src/styles/color";
import MemberSignupStepOne from "./MemberSignupStepOne";
import MemberSignupStepTwo from "./MemberSignupStepTwo";

type MemberSignUpResourceType = {
  companyName: string;
  email: string;
  corpCode: string;
  exporterId: number | null;
  mainCategoryCodeItemNames: string[];
  autoApproveNewMembers?: boolean;
};

export const MemberSignupStepContext = createContext<
  | {
      step: number;
      onStepNext: () => void;
      onStepPrev: () => void;
    }
  | undefined
>(undefined);

const { Provider: MemberStepContextProvider } = MemberSignupStepContext;

export const MemberSignUpResourceContext = createContext<
  | (MemberSignUpResourceType & {
      onResourceChange: (key: keyof MemberSignUpResourceType, data: any) => void;
    })
  | undefined
>(undefined);

const MemberSignupPage: React.FC = () => {
  const { t } = useTranslation(["signup", "common"]);
  const [step, setStep] = useState(1);

  const [memberSignUpResource, setMemberSignUpResource] =
    useState<MemberSignUpResourceType>({
      companyName: "",
      email: "",
      corpCode: "",
      exporterId: null,
      mainCategoryCodeItemNames: [],
      autoApproveNewMembers: undefined,
    });

  const renderContent = () => {
    switch (step) {
      case 1:
        return <MemberSignupStepOne />;
      case 2:
        return <MemberSignupStepTwo />;
      default:
        return <MemberSignupStepOne />;
    }
  };

  const handleMemberSignUpResourceChange = useCallback(
    (key: keyof MemberSignUpResourceType, data: any) => {
      setMemberSignUpResource((prev) => ({
        ...prev,
        [key]: data,
      }));
    },
    []
  );

  const handleStepNext = () => setStep((prev) => prev + 1);
  const handleStepPrev = () => setStep((prev) => prev - 1);

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
  };

  const resourceContextValue = useMemo(
    () => ({
      ...memberSignUpResource,
      onResourceChange: handleMemberSignUpResourceChange,
    }),
    [memberSignUpResource, handleMemberSignUpResourceChange]
  );

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <Layout>
      <PublicPageHeader />

      <MainSection>
        {step === 1 && (
          <TitleSection>
            <Typo as="h1" typoType="d3" color="gray1">
              {t("signup:v2.workspaceFound")}
            </Typo>
          </TitleSection>
        )}

        <MemberStepContextProvider
          value={{
            step,
            onStepNext: handleStepNext,
            onStepPrev: handleStepPrev,
          }}
        >
          <MemberSignUpResourceContext.Provider value={resourceContextValue}>
            {renderContent()}
          </MemberSignUpResourceContext.Provider>
        </MemberStepContextProvider>
      </MainSection>

      <PublicPageFooter />
    </Layout>
  );
};

export default MemberSignupPage;

const Layout = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: ${colorSet.white};
`;

const MainSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  padding: 60px 40px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 24px 0;

  h1 {
    text-align: center;
  }
`;
